/**
 * TextStyleManager - Manages text styles and font loading.
 *
 * Wraps the stateless utility functions from utils/textStyle.ts and provides
 * a stateful API for managing text styles in the drawing.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { getAllTextStyles, ensureTextStyleExists } from "../database/textStyle";
import { loadFontToFS, loadFontFromURL, getLoadedFonts } from "../core/font";
import type { Textstyle } from "@/types";

export class TextStyleManager {
  constructor(private viewer: DRDrawingViewer) {}

  /**
   * Get all text styles in the database.
   */
  getAll(): Textstyle[] {
    if (!this.viewer.database) return [];
    return getAllTextStyles(this.viewer.module, this.viewer.database);
  }

  /**
   * Create or update a text style.
   * @param style Text style configuration
   * @returns The style name
   */
  create(style: Textstyle): string {
    if (!this.viewer.database) {
      console.error("[TextStyleManager] Not initialized");
      return "";
    }
    ensureTextStyleExists(this.viewer.module, this.viewer.database, style);
    return style.name;
  }

  /**
   * Load a font file into the virtual filesystem and register with ODA.
   * @param fontName The filename (e.g., "myfont.ttf")
   * @param fontData The font file data
   */
  loadFont(fontName: string, fontData: Uint8Array): void {
    loadFontToFS(this.viewer.module, fontName, fontData);
    // Register TTF fonts with ODA services so they can be found by typeface
    if (fontName.toLowerCase().endsWith(".ttf")) {
      this.viewer.registerTTFFont(fontName);
    }
  }

  /**
   * Load a font from a URL and register with ODA.
   * @param url The URL to fetch the font from
   * @param fontName Optional custom name
   */
  async loadFontFromURL(url: string, fontName?: string): Promise<void> {
    const name = await loadFontFromURL(this.viewer.module, url, fontName);
    // Register TTF fonts with ODA services so they can be found by typeface
    if (name.toLowerCase().endsWith(".ttf")) {
      this.viewer.registerTTFFont(name);
    }
  }

  /**
   * Get list of loaded fonts.
   */
  getLoadedFonts(): string[] {
    return getLoadedFonts();
  }
}
